// The Sales page's wholesale tabs: Trade (the pipeline of businesses),
// Invoices and Trade prices. Works from useTradeData() and keeps the
// half-done work here (the invoice being edited, unsaved price changes) so
// flicking between tabs doesn't lose it.
import { useState } from 'react';
import { Button, PageHeader } from '../lib/ui';
import { invoiceToForm, newInvoiceForm, duplicateForm } from '../lib/trade';
import { useFlash } from './TradeCommon';
import TradePipeline from './TradePipeline';
import TradeAccountView from './TradeAccountView';
import TradeAccountModal from './TradeAccountModal';
import TradeInvoiceList from './TradeInvoiceList';
import TradeInvoiceEditor from './TradeInvoiceEditor';
import TradeInvoiceSettings from './TradeInvoiceSettings';
import TradePrices from './TradePrices';
import './Trade.css';

const SUBTITLES = {
  trade:    'Businesses you sell to wholesale, from the first chat to regular orders',
  invoices: 'Trade invoices: make them, send them, mark them paid',
  prices:   'Your trade price list. Edit it here, then save it as a PDF to print or send',
};

export default function TradeSection({ tab, setTab, header, trade, today }) {
  const { flash, banner } = useFlash();
  const [accountId, setAccountId] = useState(null);       // business open on the Trade tab
  const [editor, setEditor] = useState(null);              // invoice being edited: { id, form, dirty }
  const [priceDraft, setPriceDraft] = useState(null);      // unsaved price list
  const [accountForm, setAccountForm] = useState(null);    // {} = add, { account } = edit
  const [showSettings, setShowSettings] = useState(false);

  const account = trade.accounts.find(a => a._id === accountId) || null;
  const ready = trade.status === 'ready';

  const closeEditor = () => {
    if (editor?.dirty && !window.confirm('Discard your changes to this invoice?')) return false;
    setEditor(null);
    return true;
  };

  const openInvoice = (id) => {
    if (editor?.id !== id) {
      if (editor && !closeEditor()) return;
      const invoice = trade.invoices.find(i => i._id === id);
      if (!invoice) return;
      setEditor({ id, form: invoiceToForm(invoice), dirty: false });
    }
    setTab('invoices');
  };

  const newInvoice = (forAccountId) => {
    if (editor && !closeEditor()) return;
    const forAccount = trade.accounts.find(a => a._id === forAccountId);
    setEditor({ id: null, form: newInvoiceForm({ account: forAccount, settings: trade.settings, today }), dirty: false });
    setTab('invoices');
  };

  const duplicateInvoice = (invoice) => {
    if (!closeEditor()) return;
    const forAccount = trade.accounts.find(a => a._id === invoice.account);
    setEditor({ id: null, form: duplicateForm(invoice, { account: forAccount, settings: trade.settings, today }), dirty: true });
  };

  const openAccount = (id) => {
    setAccountId(id);
    setTab('trade');
  };

  // Clicking the tab you're already on takes you back to its list.
  const onTabChange = (t) => {
    if (t === tab && t === 'trade') setAccountId(null);
    if (t === tab && t === 'invoices' && editor) closeEditor();
    header.onTabChange(t);
  };

  const accountSaved = (saved) => {
    trade.putAccount(saved);
    const { account: editing, forInvoice } = accountForm;
    setAccountForm(null);
    flash(editing ? 'Saved' : `Added ${saved.businessName}`);
    if (forInvoice) setEditor(e => e && { ...e, form: { ...e.form, account: saved._id }, dirty: true });
    else if (!editing) setAccountId(saved._id);
  };

  let actions = null;
  if (ready && tab === 'trade' && !account) {
    actions = <Button variant="primary" onClick={() => setAccountForm({})}>+ Add business</Button>;
  } else if (ready && tab === 'invoices' && !editor) {
    actions = (
      <>
        <Button onClick={() => setShowSettings(true)}>Invoice settings</Button>
        <Button variant="primary" onClick={() => newInvoice(null)}>+ New invoice</Button>
      </>
    );
  }

  return (
    <div className="dashboard-container">
      <PageHeader title="Sales" subtitle={SUBTITLES[tab]} {...header} onTabChange={onTabChange} actions={actions} />

      {trade.status === 'loading' && <div className="loader">Loading trade sales...</div>}
      {trade.status === 'missing' && (
        <div className="ui-banner ui-banner--warn">
          The server doesn't know about trade sales yet. Deploy the latest garageFarmServer (it adds routes/trade.js), then reload this page.
        </div>
      )}
      {trade.status === 'error' && (
        <div className="ui-banner ui-banner--bad trade-row">
          Couldn't load trade sales: {trade.error}
          <Button size="sm" onClick={trade.reload}>Try again</Button>
        </div>
      )}

      {ready && tab === 'trade' && (account ? (
        <TradeAccountView
          account={account}
          invoices={trade.invoices.filter(i => i.account === account._id)}
          trade={trade}
          today={today}
          flash={flash}
          onBack={() => setAccountId(null)}
          onEdit={() => setAccountForm({ account })}
          onNewInvoice={() => newInvoice(account._id)}
          onOpenInvoice={openInvoice}
        />
      ) : (
        <TradePipeline
          trade={trade}
          today={today}
          flash={flash}
          onOpen={setAccountId}
          onAdd={() => setAccountForm({})}
        />
      ))}

      {ready && tab === 'invoices' && (editor ? (
        <TradeInvoiceEditor
          editor={editor}
          setEditor={setEditor}
          trade={trade}
          today={today}
          flash={flash}
          onClose={closeEditor}
          onDuplicate={duplicateInvoice}
          onOpenAccount={openAccount}
          onAddAccount={() => setAccountForm({ forInvoice: true })}
          onOpenSettings={() => setShowSettings(true)}
        />
      ) : (
        <TradeInvoiceList trade={trade} today={today} onOpen={openInvoice} onNew={() => newInvoice(null)} />
      ))}

      {ready && tab === 'prices' && (
        <TradePrices saved={trade.priceList} draft={priceDraft} setDraft={setPriceDraft} onSaved={trade.setPriceList} flash={flash} />
      )}

      {accountForm && (
        <TradeAccountModal account={accountForm.account} onClose={() => setAccountForm(null)} onSaved={accountSaved} />
      )}
      {showSettings && (
        <TradeInvoiceSettings
          settings={trade.settings}
          onClose={() => setShowSettings(false)}
          onSaved={(s) => { trade.setSettings(s); setShowSettings(false); flash('Invoice settings saved'); }}
        />
      )}
      {banner}
    </div>
  );
}
